import { errorMessagesHandling } from "@utils/errorHandler";
import { axiosService, axiosFormClient } from "./AxiosService";
import { toastService } from "@services/ToastService";

interface CountryProps {
  id: number;
  name: string;
  eu: boolean;
  code: string;
}

interface ProjectOverview {
  id: string;
  name: string;
  icon?: string;
  description: string;
  countries?: CountryProps[];
  start_date?: string;
  end_date?: string;
  funders?: string;
  budget?: string;
}

export const apiService = {
  get: async (
    path: string,
    config = {},
    errorText?: string,
    entityName?: string,
    ignoreToastError?: boolean
  ) => {
    try {
      const response = await axiosService.get(path, config);
      if (response?.status === 200 || response?.status === 201) {
        return response;
      }
    } catch (error) {
      if (ignoreToastError === false || ignoreToastError === undefined) {
        errorMessagesHandling(error, errorText, entityName);
      } else {
        throw error;
      }
    }
  },

  post: async (path: string, data?: any, config = {}, errorText?: string) => {
    try {
      const response = await axiosService.post(path, data, config);

      if (response?.data) {
        return response.data;
      } else if (response?.status === 200 || response?.status === 201) {
        return response;
      }
    } catch (error) {
      errorMessagesHandling(error, errorText);
      throw error;
    }
  },

  put: async (path: string, data: any, config = {}, errorText?: string) => {
    try {
      const response = await axiosService.put(path, data, config);

      if (response?.data) {
        toastService.success("Data changed successfully");
        return response.data;
      }
    } catch (error) {
      errorMessagesHandling(error, errorText);
    }
  },

  patch: async (path: string, data: any, config = {}, errorText?: string) => {
    try {
      const response = await axiosService.patch(path, data, config);

      if (response?.data) {
        toastService.success("Data changed successfully");
        return response.data;
      }
    } catch (error) {
      errorMessagesHandling(error, errorText);
      toastService.error("Action failed");
    }
  },

  precheckFile: async (
    projectId: string | undefined,
    filename: string,
    folderId?: number,
    errorText?: string
  ) => {
    if (!projectId || !filename) {
      throw new Error("Project ID and filename are required.");
    }

    const fileCheckUrl = `/projects/${projectId}/precheck-file/?filename=${encodeURIComponent(
      filename
    )}&local_folder_id=${folderId ?? 0}`;

    try {
      const response = await axiosService.get(fileCheckUrl, {
        headers: {
          Accept: "application/json",
        },
      });
      return response.data; // Expecting true/false or file metadata
    } catch (error) {
      console.error("File Precheck Error:", error);
      errorMessagesHandling(error, errorText);
      throw error;
    }
  },

  uploadFile: async (
    projectId: string | undefined,
    file: File,
    overwrite = false,
    folderId?: number,
    errorText?: string
  ) => {
    const formData = new FormData();
    const fileUrl = `/projects/${projectId}/upload-file/?overwrite=${overwrite}&local_folder_id=${
      folderId ?? 0
    }`;

    formData.append("uploaded_file", file);

    try {
      const response = await axiosService.post(fileUrl, formData, {
        headers: {
          Accept: "application/json",
          "Content-Type": `multipart/form-data`,
        },
      });
      return response;
    } catch (error) {
      console.error("File Upload Error:", error);
      errorMessagesHandling(error, errorText);
      throw error;
    }
  },

  getFiles: async (
    projectId: string | undefined,
    sharedToken: string | undefined,
    isPublicShared: boolean | undefined,
    {
      offset = 0,
      limit = 9,
      scopeOrFolderIds = "all",
    }: {
      offset?: number;
      limit?: number;
      scopeOrFolderIds?: "all" | string[];
    }
  ) => {
    if (!projectId) throw new Error("projectId is required");

    // Build base path (shared vs normal)
    const basePath = sharedToken
      ? `/projects/p/${sharedToken}/${projectId}/files`
      : `/projects/${projectId}/files/`;

    // Build query params cleanly
    const params = new URLSearchParams();
    params.set("offset", String(offset));
    params.set("limit", String(limit));

    if (scopeOrFolderIds === "all") {
      params.set("scope", "all");
    } else if (Array.isArray(scopeOrFolderIds) && scopeOrFolderIds.length > 0) {
      params.set("local_folder_ids", scopeOrFolderIds.join(","));
    } else {
      params.set("scope", "all");
    }

    const url = `${basePath}?${params.toString()}`;

    try {
      const response = await axiosService.get(url);
      return response.data;
    } catch (error) {
      if (isPublicShared === false || isPublicShared === undefined) {
        errorMessagesHandling(error);
        return { files: [], total_count: 0 };
      }
      throw error;
    }
  },

  downloadFile: async (
    projectId: string | undefined,
    fileId: string,
    fileName: string,
    fileType: string,
    sharedToken?: string
  ) => {
    try {
      const fileEndpoint = `${
        sharedToken
          ? `/projects/p/${sharedToken}/${projectId}/files/${fileId}`
          : `/projects/${projectId}/files/${fileId}`
      }`;
      const response = await axiosService.get(
        `/projects/${projectId}/files/${fileId}/${
          sharedToken ? `?shared_token=${sharedToken}` : ""
        }`,
        {
          responseType: "blob",
        }
      );

      if (response.status === 200) {
        const url = window.URL.createObjectURL(
          new Blob([response.data], { type: fileType })
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${fileName}`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        throw new Error("Failed to download file");
      }
    } catch (error) {
      console.error("Download File Error:", error);
      console.error("File download Error:", error);
      errorMessagesHandling(error);
      throw error;
    }
  },

  deleteFile: async (projectId: string | undefined, fileIds: string[]) => {
    try {
      let deleteUrl = `/projects/${projectId}/files/?files=`;
      fileIds.forEach((fileId, index) => {
        deleteUrl += `${fileId}`;
        if (index < fileIds.length - 1) {
          deleteUrl += `&files=`;
        }
      });
      const response = await axiosService.delete(deleteUrl);
      return response.data;
    } catch (error) {
      console.error("Delete File Error:", error);
      errorMessagesHandling(error);
      throw error;
    }
  },

  updateProject: async (
    projectId: string,
    updatedFields: Partial<ProjectOverview>
  ) => {
    try {
      const response = await fetch(`/api/projects/${projectId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updatedFields),
      });

      if (!response.ok) {
        throw new Error("Failed to update project");
      }

      return await response.json();
    } catch (error) {
      console.error("Error updating project:", error);
      throw error;
    }
  },

  delete: async (
    path: string,
    data?: any,
    config = {},
    errorText?: string,
    entityName = "Item"
  ) => {
    try {
      const response = await axiosService.delete(path, data);

      if (response) {
        toastService.success(`${entityName} successfully deleted`);
      }
    } catch (error) {
      errorMessagesHandling(error, errorText);
      toastService.error("Action failed");
    }
  },

  // Add more methods as needed (put, delete, etc.)
};

export const formService = {
  get: async (path: string, config = {}) => {
    try {
      const response = await axiosFormClient.get(path, config);

      if (response?.data) {
        return response.data;
      }
    } catch (error) {
      console.error("API GET Error:", error);
      throw error;
    }
  },

  post: async (path: string, data: any, config = {}) => {
    try {
      const response = await axiosFormClient.post(path, data, config);

      return response.data;
    } catch (error) {
      console.error("API POST Error:", error);
      throw error;
    }
  },

  // Add more methods as needed (put, delete, etc.)
};
